import { useQuery } from '@tanstack/react-query'
import {
  ArrowDownRight,
  ArrowUpRight,
  DollarSign,
  MousePointer,
  TrendingUp,
  Users,
} from 'lucide-react'
import { useId } from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { publisherApi } from '../lib/api'

export function Dashboard() {
  const gradientId = useId()
  const { data: stats, isLoading } = useQuery({
    queryKey: ['stats'],
    queryFn: publisherApi.getStats,
  })

  const mockChartData = [
    { date: 'Mon', clicks: 120, conversions: 12, earnings: 540 },
    { date: 'Tue', clicks: 150, conversions: 18, earnings: 810 },
    { date: 'Wed', clicks: 180, conversions: 22, earnings: 990 },
    { date: 'Thu', clicks: 140, conversions: 15, earnings: 675 },
    { date: 'Fri', clicks: 200, conversions: 28, earnings: 1260 },
    { date: 'Sat', clicks: 220, conversions: 32, earnings: 1440 },
    { date: 'Sun', clicks: 190, conversions: 25, earnings: 1125 },
  ]

  const statsCards = [
    {
      title: 'Total Clicks',
      value: stats?.totalClicks || 0,
      icon: MousePointer,
      change: '+12.5%',
      changeType: 'positive',
      color: 'blue',
    },
    {
      title: 'Unique Visitors',
      value: stats?.uniqueClicks || 0,
      icon: Users,
      change: '+8.2%',
      changeType: 'positive',
      color: 'green',
    },
    {
      title: 'Conversions',
      value: stats?.conversions || 0,
      icon: TrendingUp,
      change: '-2.4%',
      changeType: 'negative',
      color: 'purple',
    },
    {
      title: 'Earnings',
      value: `$${stats?.earnings?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      change: '+23.1%',
      changeType: 'positive',
      color: 'indigo',
    },
  ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">Track your performance and earnings</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat) => (
          <div
            key={`stat-${stat.title}`}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                <div className="flex items-center mt-2">
                  {stat.changeType === 'positive' ? (
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                  )}
                  <span
                    className={`text-sm font-medium ${
                      stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">vs last week</span>
                </div>
              </div>
              <div className={`p-3 bg-${stat.color}-50 rounded-lg`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Click Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={mockChartData}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="clicks"
                stroke="#6366f1"
                fillOpacity={1}
                fill={`url(#${gradientId})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Earnings</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mockChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip formatter={(value) => `$${value}`} />
              <Bar dataKey="earnings" fill="#10b981" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Conversion Rate Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Conversion Performance</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">Clicks</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
              <span className="text-sm text-gray-600">Conversions</span>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={mockChartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="clicks"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={{ fill: '#3b82f6' }}
            />
            <Line
              type="monotone"
              dataKey="conversions"
              stroke="#a855f7"
              strokeWidth={2}
              dot={{ fill: '#a855f7' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Quick Stats */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Pending Payment</h3>
            <p className="text-3xl font-bold mt-2">
              ${stats?.pendingPayment?.toFixed(2) || '0.00'}
            </p>
            <p className="text-indigo-100 mt-1">
              Conversion Rate: {stats?.conversionRate?.toFixed(2) || '0.00'}%
            </p>
          </div>
          <div className="text-right">
            <p className="text-indigo-100">Last Payment</p>
            <p className="text-lg font-medium">
              {stats?.lastPaymentDate
                ? new Date(stats.lastPaymentDate).toLocaleDateString()
                : 'No payments yet'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
